/**
 * The value of a dimension typed as plain arithmetic (`600+18`, `(1200-36)/2`): numbers,
 * + - * / and brackets, the same the backend accepts. Null when it is not such a sum, so a
 * check on it is skipped and the format check reports the input instead.
 */
export const evaluateArithmetic = (raw: unknown): number | null => {
	const tokens = String(raw ?? '').match(/\d+(?:\.\d+)?|[-+*/()]|\S/g);
	if (!tokens) {
		return null;
	}
	let position = 0;

	const parsePrimary = (): number => {
		const token = tokens[position++];
		if (token === '(') {
			const value = parseSum();
			if (tokens[position++] !== ')') {
				throw new Error('unbalanced');
			}
			return value;
		}
		if (token === '-') {
			return -parsePrimary();
		}
		const value = Number(token);
		if (token === undefined || Number.isNaN(value)) {
			throw new Error('not a number');
		}
		return value;
	};

	const parseProduct = (): number => {
		let value = parsePrimary();
		while (tokens[position] === '*' || tokens[position] === '/') {
			value = tokens[position++] === '*' ? value * parsePrimary() : value / parsePrimary();
		}
		return value;
	};

	const parseSum = (): number => {
		let value = parseProduct();
		while (tokens[position] === '+' || tokens[position] === '-') {
			value = tokens[position++] === '+' ? value + parseProduct() : value - parseProduct();
		}
		return value;
	};

	try {
		const value = parseSum();
		return position === tokens.length && Number.isFinite(value) ? value : null;
	} catch {
		return null;
	}
};
